import { Configuration, PromotionsApi } from '@/api';
import useGetPromotionQuery from '@/api/queries/useGetPromotionQuery';
import useGetSkusQuery from '@/api/queries/useGetSkusQuery';
import useGetSupplierQuery from '@/api/queries/useGetSupplierQuery';
import Button from '@/components/Button';
import Confirm from '@/components/Confirm';
import CustomToast from '@/components/CustomToast';
import SpinnerIcon from '@/components/icons/SpinnerIcon';
import { PromotionCreate, PromotionUpdate } from '@/models/Promotion';
import { useAuth } from '@/modules/auth/contexts/AuthContext';
import PromotionForm from '@/modules/product/components/PromotionForm';
import { PromotionListPath } from '@/modules/product/paths';
import SettingsHeader from '@/modules/settings/components/SettingsHeader';
import { PercentBadgeIcon, XMarkIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { useNavigate, useParams } from 'react-router-dom';

const PromotionEdit = () => {
  const navigate = useNavigate();
  const { promotionId } = useParams();

  const {
    promotion,
    isLoading: isLoadingPromotion,
    error,
    invalidateQuery,
  } = useGetPromotionQuery(promotionId);

  const { skus, isLoading: isLoadingSkus } = useGetSkusQuery(promotion?.skuIds);

  const { currentUser } = useAuth();

  const { supplierId } = currentUser();

  const { supplier, isLoading: isLoadingSupplier } = useGetSupplierQuery(supplierId);

  const promotionsApi = new PromotionsApi(new Configuration(useAuth));

  const handleSubmit = async (promotionInput: PromotionCreate | PromotionUpdate) => {
    if (!promotion) {
      return;
    }

    const updatedPromotion: PromotionUpdate = {
      ...promotionInput,
      id: promotion.id,
    };

    try {
      await promotionsApi.updatePromotion(promotion.id, updatedPromotion);

      toast.custom((t) => (
        <CustomToast
          id={t.id}
          message={`Promotion "${updatedPromotion.name}" updated successfully!`}
          visible={t.visible}
        />
      ));

      await invalidateQuery(true);

      navigate(PromotionListPath);
    } catch (error) {
      console.error('Error while updating promotion:', error);
    }
  };

  const handleOnDelete = async () => {
    if (!promotion?.id) return;

    try {
      await promotionsApi.deletePromotion(promotion.id);

      toast.custom((t) => (
        <CustomToast
          id={t.id}
          message={`Promotion "${promotion.name}" deleted successfully!`}
          visible={t.visible}
        />
      ));

      navigate(PromotionListPath);
    } catch (error) {
      console.error('Failed to delete promotion:', error);
    }
  };

  if (isLoadingPromotion || isLoadingSkus || isLoadingSupplier) {
    return (
      <div className="flex justify-center py-20">
        <SpinnerIcon />
      </div>
    );
  }

  if (!promotion || error) {
    return null;
  }

  return (
    <>
      <SettingsHeader title="Edit Promotion" icon={<PercentBadgeIcon className="w-6 h-6" />}>
        <Confirm
          onConfirm={handleOnDelete}
          title="Delete Promotion"
          message={`Are you sure you want to delete promotion "${promotion.name}"? This action cannot be undone.`}
          danger
        >
          {(handleClick, loading) => (
            <Button
              onClick={handleClick}
              icon={<XMarkIcon className="h-5 w-5 mr-1" aria-hidden="true" />}
              loading={loading}
              variant="delete"
            >
              Delete Promotion
            </Button>
          )}
        </Confirm>
      </SettingsHeader>

      <PromotionForm
        onSubmit={handleSubmit}
        promotion={promotion}
        skus={skus}
        currencyCode={supplier?.currencyCode}
      />
    </>
  );
};

export default PromotionEdit;
